import { useEffect } from 'react';
import { IProductData } from '@/utils';
import { IS_PREVIEW } from '@/utils/env';
import { 
  pushToElevarDataLayer, 
  getUserProperties, 
  transformProductToElevar,
  setReferrerList 
} from './utils';
import { ElevarViewItemListEvent, ElevarViewSearchResultsEvent, ElevarSelectItemEvent } from './types';

/**
 * Elevar collection page view tracking hook.
 * Fires dl_view_item_list event when user views a collection/category page.
 */
export const useElevarViewItemList = (products: IProductData[], listPath?: string) => {
  useEffect(() => {
    if (IS_PREVIEW) return;
    if (!products || products.length === 0) return;

    const userProperties = getUserProperties();
    const currentPath = listPath || (typeof window !== 'undefined' ? window.location.pathname : '');
    
    // Set this as the referrer list for future product interactions
    setReferrerList(currentPath);
    
    // Transform products to impressions (limit to prevent 4000 char limit)
    const maxProducts = Math.min(products.length, 20); // Limit to 20 products
    const impressions = products.slice(0, maxProducts).map((product, index) => 
      transformProductToElevar(product, null, false, index + 1, currentPath)
    );

    const event: ElevarViewItemListEvent = {
      event: 'dl_view_item_list',
      user_properties: userProperties,
      ecommerce: {
        currencyCode: 'USD',
        impressions
      }
    };

    pushToElevarDataLayer(event);
  }, [products, listPath]);
};

/**
 * Elevar search results page view tracking hook.
 * Fires dl_view_search_results event when user views search results.
 */
export const useElevarViewSearchResults = (products: IProductData[], searchQuery?: string) => {
  useEffect(() => {
    if (IS_PREVIEW) return;
    if (!products || products.length === 0) return;

    const userProperties = getUserProperties();
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    
    // Set this as the referrer list for future product interactions
    setReferrerList(currentPath);
    
    // Transform products to impressions (limit to prevent 4000 char limit)
    const maxProducts = Math.min(products.length, 20); // Limit to 20 products
    const impressions = products.slice(0, maxProducts).map((product, index) => 
      transformProductToElevar(product, null, false, index + 1, 'search results')
    );

    const event: ElevarViewSearchResultsEvent = {
      event: 'dl_view_search_results',
      user_properties: userProperties,
      ecommerce: {
        currencyCode: 'USD',
        actionField: {
          list: 'search results'
        },
        impressions
      }
    };

    pushToElevarDataLayer(event);
  }, [products, searchQuery]);
};

/**
 * Elevar product click tracking from collection or search page.
 * Call this function when user clicks on a product from a list.
 */
export const handleElevarSelectItem = (
  product: IProductData,
  position: number,
  listPath?: string
) => {
  if (IS_PREVIEW) return;

  const userProperties = getUserProperties();
  const currentPath = listPath || (typeof window !== 'undefined' ? window.location.pathname : '');
  
  const elevarProduct = transformProductToElevar(product, null, false, position, currentPath);

  const event: ElevarSelectItemEvent = {
    event: 'dl_select_item',
    user_properties: userProperties,
    ecommerce: {
      currencyCode: 'USD',
      click: {
        actionField: {
          list: currentPath,
          action: 'click'
        },
        products: [elevarProduct]
      }
    }
  };

  pushToElevarDataLayer(event);
};